import { apiRequest } from './api';
import api from './api';
import type {
  MediaItem,
  MediaListResponse,
  CreateMediaRequest,
  UpdateMediaRequest,
  MediaFilterOptions
} from '../types/media';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
const API_PREFIX = process.env.REACT_APP_API_PREFIX || '/api/cms';

// 레이아웃 목록 조회
export const getLayouts = async (): Promise<string[]> => {
  try {
    const response = await apiRequest('GET', '/media/layouts');
    return response.data || [];
  } catch (error) {
    console.error('레이아웃 목록 조회 실패:', error);
    throw error;
  }
};

// 미디어 목록 조회
export const getMediaList = async (filters: MediaFilterOptions = {}): Promise<MediaListResponse> => {
  try {
    const params = new URLSearchParams();

    if (filters.exposureType && filters.exposureType !== 'all') {
      params.append('exposure', filters.exposureType);
    }
    if (filters.searchKeyword) {
      params.append('search', filters.searchKeyword);
    }
    if (filters.page) {
      params.append('page', filters.page.toString());
    }
    if (filters.limit) {
      params.append('limit', filters.limit.toString());
    }

    const queryString = params.toString();
    const url = `/media${queryString ? `?${queryString}` : ''}`;

    const response = await apiRequest('GET', url);
    return response;
  } catch (error) {
    console.error('미디어 목록 조회 실패:', error);
    throw error;
  }
};

// 미디어 상세 조회
export const getMediaDetail = async (id: number): Promise<MediaItem> => {
  try {
    const response = await apiRequest('GET', `/media/${id}`);
    return response.data;
  } catch (error) {
    console.error('미디어 상세 조회 실패:', error);
    throw error;
  }
};

// GCS 업로드용 Signed URL 생성
const generateUploadUrl = async (fileName: string, contentType: string, folder: string = 'videos'): Promise<{ uploadUrl: string; fileName: string; expires: string }> => {
  try {
    const response = await apiRequest('POST', '/upload/signed-url', {
      fileName,
      contentType,
      folder
    });
    return response.data;
  } catch (error) {
    console.error('업로드용 Signed URL 생성 실패:', error);
    throw error;
  }
};

// GCS로 직접 파일 업로드
const uploadToGCS = async (file: File, uploadUrl: string): Promise<void> => {
  try {
    await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type
      }
    });
  } catch (error) {
    console.error('GCS 업로드 실패:', error);
    throw error;
  }
};

// 미디어 생성 (GCS 직접 업로드)
export const createMedia = async (request: CreateMediaRequest): Promise<MediaItem> => {
  try {
    if (!request.videoFile) {
      throw new Error('비디오 파일이 필요합니다.');
    }

    const file = request.videoFile;
    
    // 1. 업로드용 Signed URL 생성
    const { uploadUrl, fileName: gcsFileName } = await generateUploadUrl(
      file.name,
      file.type,
      'videos'
    );

    // 2. GCS로 직접 업로드
    await uploadToGCS(file, uploadUrl);

    // 3. 서버에 메타데이터 전송
    const response = await apiRequest('POST', '/media', {
      layoutName: request.layoutName,
      exposureType: request.exposureType,
      mediaName: request.mediaName,
      content: request.content || '',
      startDate: request.startDate || null,
      endDate: request.endDate || null,
      fileName: gcsFileName,
      fileSize: file.size,
      contentType: file.type,
      originalFileName: file.name
    });

    return response.data;
  } catch (error) {
    console.error('미디어 생성 실패:', error);
    throw error;
  }
};

// 미디어 수정
export const updateMedia = async (request: UpdateMediaRequest): Promise<MediaItem> => {
  try {
    if (!request.videoFile) {
      const payload: any = {};

      if (request.layoutName !== undefined) payload.layoutName = request.layoutName;
      if (request.exposureType !== undefined) payload.exposureType = request.exposureType;
      if (request.mediaName !== undefined) payload.mediaName = request.mediaName;
      if (request.content !== undefined) payload.content = request.content;
      if (request.startDate !== undefined) payload.startDate = request.startDate;
      if (request.endDate !== undefined) payload.endDate = request.endDate;
      if (request.deleteCurrentFile !== undefined) payload.deleteCurrentFile = request.deleteCurrentFile;

      const response = await api.put(`/media/${request.id}`, payload);
      return response.data.data;
    }

    const formData = new FormData();
    if (request.layoutName !== undefined) formData.append('layoutName', request.layoutName);
    if (request.exposureType !== undefined) formData.append('exposureType', request.exposureType);
    if (request.mediaName !== undefined) formData.append('mediaName', request.mediaName);
    if (request.content !== undefined) formData.append('content', request.content);
    if (request.startDate !== undefined) formData.append('startDate', request.startDate || '');
    if (request.endDate !== undefined) formData.append('endDate', request.endDate || '');
    if (request.deleteCurrentFile !== undefined) formData.append('deleteCurrentFile', String(request.deleteCurrentFile));
    formData.append('videoFile', request.videoFile);

    const response = await api.put(`/media/${request.id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data.data;
  } catch (error) {
    console.error('미디어 수정 실패:', error);
    throw error;
  }
};

// 미디어 삭제
export const deleteMedia = async (id: number): Promise<void> => {
  try {
    await apiRequest('DELETE', `/media/${id}`);
  } catch (error) {
    console.error('미디어 삭제 실패:', error);
    throw error;
  }
};

// 미디어 노출/비노출 상태 변경
export const toggleMediaExposure = async (id: number): Promise<MediaItem> => {
  try {
    const response = await apiRequest('PATCH', `/media/${id}/toggle`);
    return response.data;
  } catch (error) {
    console.error('미디어 노출 상태 변경 실패:', error);
    throw error;
  }
};

// 미디어 일괄 삭제
export const bulkDeleteMedia = async (ids: number[]): Promise<void> => {
  try {
    await apiRequest('POST', '/media/bulk-delete', {
      ids,
    });
  } catch (error) {
    console.error('미디어 일괄 삭제 실패:', error);
    throw error;
  }
};

// 미디어 일괄 노출/비노출 상태 변경
export const bulkToggleMediaExposure = async (ids: number[], exposureType: 'show' | 'hide'): Promise<void> => {
  try {
    await apiRequest('PATCH', '/media/bulk-toggle', {
      ids,
      exposureType,
    });
  } catch (error) {
    console.error('미디어 일괄 노출 상태 변경 실패:', error);
    throw error;
  }
};

// 미디어 파일 다운로드
export const downloadMedia = async (id: number): Promise<void> => {
  try {
    const response = await api.get(`/media/${id}/download`, {
      responseType: 'blob',
    });

    // Content-Disposition 헤더에서 파일명 추출
    const contentDisposition = response.headers['content-disposition'] || '';
    let fileName = `media-${id}.mp4`;

    if (contentDisposition) {
      // filename="..." 형식에서 파일명 추출
      const match = contentDisposition.match(/filename="([^"]+)"/);
      if (match && match[1]) {
        fileName = decodeURIComponent(match[1]);
      } else {
        // filename=... 형식 (따옴표 없음) 처리
        const match2 = contentDisposition.match(/filename=([^;]+)/);
        if (match2 && match2[1]) {
          fileName = decodeURIComponent(match2[1].trim());
        }
      }
    }

    // blob을 URL로 변환
    const url = window.URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('미디어 파일 다운로드 실패:', error);
    throw error;
  }
};

const mediaService = {
  getLayouts,
  getMediaList,
  getMediaDetail,
  createMedia,
  updateMedia,
  deleteMedia,
  toggleMediaExposure,
  bulkDeleteMedia,
  bulkToggleMediaExposure,
  downloadMedia,
};

export default mediaService;